/**
 * Performance monitoring utility for image operations
 * Tracks timing metrics, error rates, and user experience metrics
 */

// Performance metrics storage
const performanceMetrics = {
  imageLoads: new Map(),
  operationAttempts: { success: 0, failure: 0 },
  errorRates: new Map(),
  userExperience: [],
  networkQuality: 'unknown',
  sessionStart: Date.now()
};

// Configuration
const CONFIG = {
  maxMetricsHistory: 1000,
  errorRateWindow: 300000, // 5 minutes
  uxMetricsWindow: 600000, // 10 minutes
  alertThresholds: {
    errorRate: 0.3, // 30% error rate
    avgLoadTime: 5000, // 5 seconds
    slowLoadTime: 10000 // 10 seconds
  }
};

/**
 * Start timing an image operation
 * @param {string} operationId - Unique identifier for the operation
 * @param {Object} metadata - Additional metadata about the operation
 * @returns {string} Timer ID for ending the operation
 */
export const startImageLoadTimer = (operationId, metadata = {}) => {
  const timerId = `${operationId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const timerData = {
    id: timerId,
    operationId,
    startTime: performance.now(),
    timestamp: Date.now(),
    metadata: {
      operationType: metadata.operationType || 'unknown',
      fileSize: metadata.fileSize,
      fileName: metadata.fileName,
      imageUrl: metadata.imageUrl,
      networkQuality: metadata.networkQuality || getNetworkQuality(),
      isLazyLoad: metadata.isLazyLoad || false,
      retryCount: metadata.retryCount || 0,
      ...metadata
    }
  };
  
  performanceMetrics.imageLoads.set(timerId, timerData);
  
  // Log start of operation for debugging
  console.log(`[Performance] Started ${metadata.operationType || 'operation'}: ${operationId}`, {
    timerId,
    metadata: timerData.metadata
  });
  
  return timerId;
};

/**
 * End timing an image operation and record metrics
 * @param {string} timerId - Timer ID from startImageLoadTimer
 * @param {Object} result - Result of the operation
 */
export const endImageLoadTimer = (timerId, result = {}) => {
  const timerData = performanceMetrics.imageLoads.get(timerId);
  if (!timerData) {
    console.warn(`[Performance] Timer not found: ${timerId}`);
    return;
  }
  
  const endTime = performance.now();
  const duration = endTime - timerData.startTime;
  
  const completedOperation = {
    ...timerData,
    endTime,
    duration,
    success: result.success || false,
    cached: result.cached || false,
    error: result.error,
    result: {
      fileSize: result.fileSize || timerData.metadata.fileSize,
      networkQuality: result.networkQuality || timerData.metadata.networkQuality,
      ...result
    }
  };
  
  // Update the stored data
  performanceMetrics.imageLoads.set(timerId, completedOperation);
  
  // Record operation attempt
  recordOperationAttempt(timerData.metadata.operationType, result.success);
  
  // Log completion
  console.log(`[Performance] Completed ${timerData.metadata.operationType}: ${timerData.operationId}`, {
    duration: `${duration.toFixed(2)}ms`,
    success: result.success,
    cached: result.cached,
    error: result.error,
    metadata: timerData.metadata
  });
  
  // Check for performance alerts
  checkPerformanceAlerts(completedOperation);
  
  // Clean up old metrics
  cleanupOldMetrics();
};

/**
 * Record an operation attempt (success or failure)
 * @param {string} operationType - Type of operation (upload, load, etc.)
 * @param {boolean} success - Whether the operation succeeded
 */
export const recordOperationAttempt = (operationType, success) => {
  if (success) {
    performanceMetrics.operationAttempts.success++;
  } else {
    performanceMetrics.operationAttempts.failure++;
  }
  
  // Update error rate tracking
  const now = Date.now();
  const errorKey = `${operationType}_${Math.floor(now / 60000)}`; // Per minute buckets
  
  if (!performanceMetrics.errorRates.has(errorKey)) {
    performanceMetrics.errorRates.set(errorKey, { success: 0, failure: 0, timestamp: now });
  }
  
  const errorData = performanceMetrics.errorRates.get(errorKey);
  if (success) {
    errorData.success++;
  } else {
    errorData.failure++;
  }
};

/**
 * Record user experience metrics
 * @param {Object} metrics - UX metrics to record
 */
export const recordUserExperience = (metrics = {}) => {
  const uxData = {
    timestamp: Date.now(),
    timeToFirstByte: metrics.timeToFirstByte,
    timeToInteractive: metrics.timeToInteractive,
    isRetry: metrics.isRetry || false,
    networkQuality: getNetworkQuality(),
    ...metrics
  };
  
  performanceMetrics.userExperience.push(uxData);
  
  // Keep only recent UX metrics
  const cutoff = Date.now() - CONFIG.uxMetricsWindow;
  performanceMetrics.userExperience = performanceMetrics.userExperience.filter(
    ux => ux.timestamp > cutoff
  );
  
  console.log('[Performance] UX metrics recorded:', uxData);
};

/**
 * Record image-related errors with context
 * @param {Error} error - The error that occurred
 * @param {Object} context - Additional context about the error
 */
export const recordImageError = (error, context = {}) => {
  const errorData = {
    timestamp: Date.now(),
    message: error.message,
    stack: error.stack,
    operationType: context.operationType || 'unknown',
    imageUrl: context.imageUrl,
    fileName: context.fileName,
    fileSize: context.fileSize,
    networkQuality: context.networkQuality || getNetworkQuality(),
    retryCount: context.retryCount || 0,
    httpStatus: context.httpStatus,
    timeout: context.timeout,
    isLazyLoad: context.isLazyLoad || false,
    ...context
  };
  
  console.error('[Performance] Image error recorded:', errorData);
  
  // Store error for analysis (could be sent to monitoring service)
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const errors = JSON.parse(localStorage.getItem('imageErrors') || '[]');
      errors.push(errorData);
      
      // Keep only recent errors (last 100)
      if (errors.length > 100) {
        errors.splice(0, errors.length - 100);
      }
      
      localStorage.setItem('imageErrors', JSON.stringify(errors));
    } catch (e) {
      console.warn('[Performance] Failed to store error data:', e);
    }
  }
};

/**
 * Get current network quality assessment
 * @returns {string} Network quality: 'good', 'slow', 'poor', 'offline', 'unknown'
 */
export const getNetworkQuality = () => {
  if (!navigator.onLine) {
    return 'offline';
  }
  
  // Use Network Information API if available
  if ('connection' in navigator) {
    const connection = navigator.connection;
    const effectiveType = connection.effectiveType;
    
    switch (effectiveType) {
      case '4g':
        return 'good';
      case '3g':
        return 'slow';
      case '2g':
      case 'slow-2g':
        return 'poor';
      default:
        return 'unknown';
    }
  }
  
  return performanceMetrics.networkQuality;
};

/**
 * Update network quality assessment
 * @param {string} quality - Network quality assessment
 */
export const updateNetworkQuality = (quality) => {
  performanceMetrics.networkQuality = quality;
  console.log(`[Performance] Network quality updated: ${quality}`);
};

/**
 * Get comprehensive performance metrics
 * @returns {Object} Current performance metrics
 */
export const getPerformanceMetrics = () => {
  const now = Date.now();
  const sessionDuration = now - performanceMetrics.sessionStart;
  
  // Calculate metrics from completed operations
  const completedOperations = Array.from(performanceMetrics.imageLoads.values())
    .filter(op => op.duration !== undefined);
  
  const successfulOps = completedOperations.filter(op => op.success);
  const failedOps = completedOperations.filter(op => !op.success);
  
  // Calculate average load times
  const avgLoadTime = successfulOps.length > 0 
    ? successfulOps.reduce((sum, op) => sum + op.duration, 0) / successfulOps.length 
    : 0;
  
  const avgUploadTime = successfulOps
    .filter(op => op.metadata.operationType === 'upload')
    .reduce((sum, op, _, arr) => arr.length > 0 ? sum + op.duration / arr.length : 0, 0);
  
  // Calculate error rates
  const totalAttempts = performanceMetrics.operationAttempts.success + performanceMetrics.operationAttempts.failure;
  const overallErrorRate = totalAttempts > 0 
    ? performanceMetrics.operationAttempts.failure / totalAttempts 
    : 0;
  
  // Recent error rate (last 5 minutes)
  const recentCutoff = now - CONFIG.errorRateWindow;
  const recentErrors = Array.from(performanceMetrics.errorRates.values())
    .filter(error => error.timestamp > recentCutoff);
  
  const recentTotalAttempts = recentErrors.reduce((sum, error) => sum + error.success + error.failure, 0);
  const recentFailures = recentErrors.reduce((sum, error) => sum + error.failure, 0);
  const recentErrorRate = recentTotalAttempts > 0 ? recentFailures / recentTotalAttempts : 0;
  
  // User experience metrics
  const recentUX = performanceMetrics.userExperience.filter(ux => ux.timestamp > recentCutoff);
  const avgTimeToFirstByte = recentUX.length > 0 
    ? recentUX.reduce((sum, ux) => sum + (ux.timeToFirstByte || 0), 0) / recentUX.length 
    : 0;
  
  return {
    session: {
      startTime: performanceMetrics.sessionStart,
      duration: sessionDuration,
      networkQuality: getNetworkQuality()
    },
    operations: {
      total: completedOperations.length,
      successful: successfulOps.length,
      failed: failedOps.length,
      pending: performanceMetrics.imageLoads.size - completedOperations.length
    },
    timing: {
      avgLoadTime: Math.round(avgLoadTime),
      avgUploadTime: Math.round(avgUploadTime),
      avgTimeToFirstByte: Math.round(avgTimeToFirstByte),
      slowLoads: successfulOps.filter(op => op.duration > CONFIG.alertThresholds.slowLoadTime).length
    },
    errorRates: {
      overall: Math.round(overallErrorRate * 100) / 100,
      recent: Math.round(recentErrorRate * 100) / 100,
      totalAttempts,
      recentAttempts: recentTotalAttempts
    },
    userExperience: {
      totalSamples: performanceMetrics.userExperience.length,
      recentSamples: recentUX.length,
      retryRate: recentUX.length > 0 
        ? recentUX.filter(ux => ux.isRetry).length / recentUX.length 
        : 0
    },
    alerts: getActiveAlerts()
  };
};

/**
 * Get detailed operation breakdown
 * @returns {Object} Detailed metrics by operation type
 */
export const getOperationBreakdown = () => {
  const completedOperations = Array.from(performanceMetrics.imageLoads.values())
    .filter(op => op.duration !== undefined);
  
  const breakdown = {};
  
  completedOperations.forEach(op => {
    const type = op.metadata.operationType;
    if (!breakdown[type]) {
      breakdown[type] = {
        total: 0,
        successful: 0,
        failed: 0,
        totalTime: 0,
        avgTime: 0,
        minTime: Infinity,
        maxTime: 0,
        cached: 0
      };
    }
    
    const stats = breakdown[type];
    stats.total++;
    
    if (op.success) {
      stats.successful++;
      stats.totalTime += op.duration;
      stats.minTime = Math.min(stats.minTime, op.duration);
      stats.maxTime = Math.max(stats.maxTime, op.duration);
      
      if (op.cached) {
        stats.cached++;
      }
    } else {
      stats.failed++;
    }
    
    stats.avgTime = stats.successful > 0 ? stats.totalTime / stats.successful : 0;
    if (stats.minTime === Infinity) stats.minTime = 0;
  });
  
  return breakdown;
};

/**
 * Check for performance alerts and log warnings
 * @param {Object} operation - Completed operation data
 */
const checkPerformanceAlerts = (operation) => {
  const alerts = [];
  
  // Check for slow load times
  if (operation.success && operation.duration > CONFIG.alertThresholds.slowLoadTime) {
    alerts.push({
      type: 'SLOW_LOAD',
      message: `Slow image load detected: ${Math.round(operation.duration)}ms`,
      operation: operation.operationId,
      threshold: CONFIG.alertThresholds.slowLoadTime
    });
  }
  
  // Check error rate
  const metrics = getPerformanceMetrics();
  if (metrics.errorRates.recent > CONFIG.alertThresholds.errorRate) {
    alerts.push({
      type: 'HIGH_ERROR_RATE',
      message: `High error rate detected: ${Math.round(metrics.errorRates.recent * 100)}%`,
      rate: metrics.errorRates.recent,
      threshold: CONFIG.alertThresholds.errorRate
    });
  }
  
  // Log alerts
  alerts.forEach(alert => {
    console.warn(`[Performance Alert] ${alert.type}: ${alert.message}`, alert);
  });
  
  return alerts;
};

/**
 * Get currently active performance alerts
 * @returns {Array} Array of active alerts
 */
const getActiveAlerts = () => {
  const alerts = [];
  
  // Calculate metrics directly to avoid circular dependency
  const now = Date.now();
  const recentCutoff = now - CONFIG.errorRateWindow;
  
  // Recent error rate calculation
  const recentErrors = Array.from(performanceMetrics.errorRates.values())
    .filter(error => error.timestamp > recentCutoff);
  
  const recentTotalAttempts = recentErrors.reduce((sum, error) => sum + error.success + error.failure, 0);
  const recentFailures = recentErrors.reduce((sum, error) => sum + error.failure, 0);
  const recentErrorRate = recentTotalAttempts > 0 ? recentFailures / recentTotalAttempts : 0;
  
  // High error rate alert
  if (recentErrorRate > CONFIG.alertThresholds.errorRate) {
    alerts.push({
      type: 'HIGH_ERROR_RATE',
      severity: 'warning',
      message: `Error rate is ${Math.round(recentErrorRate * 100)}% (threshold: ${Math.round(CONFIG.alertThresholds.errorRate * 100)}%)`,
      value: recentErrorRate,
      threshold: CONFIG.alertThresholds.errorRate
    });
  }
  
  // Average load time calculation
  const completedOperations = Array.from(performanceMetrics.imageLoads.values())
    .filter(op => op.duration !== undefined);
  const successfulOps = completedOperations.filter(op => op.success);
  const avgLoadTime = successfulOps.length > 0 
    ? successfulOps.reduce((sum, op) => sum + op.duration, 0) / successfulOps.length 
    : 0;
  
  // Slow average load time alert
  if (avgLoadTime > CONFIG.alertThresholds.avgLoadTime) {
    alerts.push({
      type: 'SLOW_AVERAGE_LOAD',
      severity: 'info',
      message: `Average load time is ${Math.round(avgLoadTime)}ms (threshold: ${CONFIG.alertThresholds.avgLoadTime}ms)`,
      value: avgLoadTime,
      threshold: CONFIG.alertThresholds.avgLoadTime
    });
  }
  
  // Network quality alert
  const networkQuality = getNetworkQuality();
  if (networkQuality === 'poor' || networkQuality === 'offline') {
    alerts.push({
      type: 'POOR_NETWORK',
      severity: networkQuality === 'offline' ? 'error' : 'warning',
      message: `Network quality is ${networkQuality}`,
      value: networkQuality
    });
  }
  
  return alerts;
};

/**
 * Clean up old metrics to prevent memory leaks
 */
const cleanupOldMetrics = () => {
  const now = Date.now();
  const cutoff = now - (CONFIG.maxMetricsHistory * 60000); // Convert minutes to ms
  
  // Clean up old image load timers
  for (const [timerId, timerData] of performanceMetrics.imageLoads.entries()) {
    if (timerData.timestamp < cutoff) {
      performanceMetrics.imageLoads.delete(timerId);
    }
  }
  
  // Clean up old error rates
  for (const [errorKey, errorData] of performanceMetrics.errorRates.entries()) {
    if (errorData.timestamp < cutoff) {
      performanceMetrics.errorRates.delete(errorKey);
    }
  }
  
  // UX metrics are already cleaned up in recordUserExperience
};

/**
 * Export performance data for external monitoring
 * @returns {Object} Exportable performance data
 */
export const exportPerformanceData = () => {
  const metrics = getPerformanceMetrics();
  const breakdown = getOperationBreakdown();
  
  return {
    timestamp: Date.now(),
    session: metrics.session,
    summary: {
      operations: metrics.operations,
      timing: metrics.timing,
      errorRates: metrics.errorRates,
      userExperience: metrics.userExperience
    },
    breakdown,
    alerts: metrics.alerts,
    networkQuality: getNetworkQuality()
  };
};

/**
 * Reset all performance metrics (useful for testing)
 */
export const resetPerformanceMetrics = () => {
  performanceMetrics.imageLoads.clear();
  performanceMetrics.operationAttempts = { success: 0, failure: 0 };
  performanceMetrics.errorRates.clear();
  performanceMetrics.userExperience = [];
  performanceMetrics.networkQuality = 'unknown';
  performanceMetrics.sessionStart = Date.now();
  
  console.log('[Performance] Metrics reset');
};

/**
 * Log current performance summary to console
 */
export const logPerformanceSummary = () => {
  const metrics = getPerformanceMetrics();
  const breakdown = getOperationBreakdown();
  
  console.group('[Performance Summary]');
  console.log('Session Duration:', `${Math.round(metrics.session.duration / 1000)}s`);
  console.log('Network Quality:', metrics.session.networkQuality);
  console.log('Operations:', `${metrics.operations.successful}/${metrics.operations.total} successful`);
  console.log('Error Rate:', `${Math.round(metrics.errorRates.overall * 100)}% overall, ${Math.round(metrics.errorRates.recent * 100)}% recent`);
  console.log('Average Load Time:', `${metrics.timing.avgLoadTime}ms`);
  console.log('Slow Loads:', metrics.timing.slowLoads);
  
  if (Object.keys(breakdown).length > 0) {
    console.log('Operation Breakdown:', breakdown);
  }
  
  if (metrics.alerts.length > 0) {
    console.warn('Active Alerts:', metrics.alerts);
  }
  
  console.groupEnd();
};

// Auto-cleanup interval
if (typeof window !== 'undefined') {
  setInterval(cleanupOldMetrics, 300000); // Clean up every 5 minutes
}

export default {
  startImageLoadTimer,
  endImageLoadTimer,
  recordOperationAttempt,
  recordUserExperience,
  recordImageError,
  getPerformanceMetrics,
  getOperationBreakdown,
  exportPerformanceData,
  resetPerformanceMetrics,
  logPerformanceSummary,
  getNetworkQuality,
  updateNetworkQuality
};